import { Component } from '@theme/component';

/**
 * Trigger for the custom header's menu drawer.
 *
 * The drawer itself is a `<theme-drawer>` (see assets/theme-drawer.js), reused
 * as-is for its focus trap / scroll lock / escape / backdrop-click / modal
 * vs. squeeze-sidebar behavior. This wrapper only exists because the
 * declarative `on:click="#id/method"` syntax can't pass the `{ enter:
 * 'inline-start' }` option through to `open()` — `toggle()` always opens
 * with the default (right-side) entrance.
 *
 * @extends {Component}
 */
class HeaderCustomDrawerTrigger extends Component {
  requiredRefs = ['button'];

  /**
   * @returns {import('@theme/theme-drawer').ThemeDrawer | null}
   */
  get #drawer() {
    const controls = this.refs.button.getAttribute('aria-controls');
    return controls
      ? /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */ (document.getElementById(controls))
      : null;
  }

  connectedCallback() {
    super.connectedCallback();
    this.#drawer?.addEventListener('theme-drawer:open', this.#syncExpanded);
    this.#drawer?.addEventListener('theme-drawer:close', this.#syncExpanded);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#drawer?.removeEventListener('theme-drawer:open', this.#syncExpanded);
    this.#drawer?.removeEventListener('theme-drawer:close', this.#syncExpanded);
  }

  toggle = () => {
    const drawer = this.#drawer;
    if (!drawer) return;

    if (drawer.isOpen) {
      drawer.close();
    } else {
      drawer.open({ enter: 'inline-start' });
    }
  };

  #syncExpanded = () => {
    this.refs.button.setAttribute('aria-expanded', String(Boolean(this.#drawer?.isOpen)));
  };
}

if (!customElements.get('header-custom-drawer-trigger')) {
  customElements.define('header-custom-drawer-trigger', HeaderCustomDrawerTrigger);
}
